import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_USER_PROFILE } from '../../graphql';
import { useAccount } from '../../context/AccountContext';
import { useAuth } from '../../context/AuthContext';
import { useTinycmsSites } from '../cms/sites';

/**
 * Shown by the content editor when the item behind /content/:id cannot be
 * loaded. The server answers the same way whether the item was deleted or
 * belongs to a site this person is not a member of (it does not reveal which),
 * so the message covers both — and says who is signed in and which site is
 * open, which is usually the whole explanation for a link someone else sent.
 */
export function ContentUnavailable({ id, error }: { id?: string; error?: string | null }) {
  const { selectedAccount, accounts } = useAccount();
  const { logout } = useAuth();
  const { data } = useQuery<{ me: { email: string } | null }>(GET_USER_PROFILE);
  const { sites } = useTinycmsSites();

  const email = data?.me?.email || null;
  const site = sites.find((s) => s.accountId === selectedAccount?.id);
  const siteName = site?.name || selectedAccount?.name || null;
  const otherSites = Math.max(0, accounts.length - 1);

  return (
    <div className="ink-unavailable">
      <h1 className="ink-h1">This item isn’t available</h1>
      <p className="ink-set-empty">
        It may have been deleted — deleted items stay in Recently deleted for 30 days — or it
        belongs to a site that {email ? <strong>{email}</strong> : 'your login'} isn’t a member of.
      </p>
      <p className="ink-set-empty">
        You’re signed in
        {email ? (
          <>
            {' '}
            as <strong>{email}</strong>
          </>
        ) : null}
        {siteName ? (
          <>
            , looking at <strong>{siteName}</strong>
            {site?.host ? <> ({site.host})</> : null}
          </>
        ) : null}
        .
        {otherSites > 0 ? (
          <>
            {' '}
            You have {otherSites} other {otherSites === 1 ? 'site' : 'sites'}; this link may belong
            to one of them.
          </>
        ) : null}
      </p>
      {error ? <p className="ink-set-empty">The server said: {error}</p> : null}
      <div className="ink-unavailable-actions">
        <Link to="/content?view=articles" className="ink-btn ink-btn-primary">
          Back to Website
        </Link>
        {otherSites > 0 ? (
          <Link to="/sites" className="ink-btn ink-btn-quiet">
            Switch site
          </Link>
        ) : null}
        <button
          type="button"
          className="ink-btn ink-btn-quiet"
          onClick={() =>
            logout('You were signed out. Sign in with the login that has access to this item.')
          }
        >
          Sign in as someone else
        </button>
      </div>
      {id ? <p className="ink-unavailable-id">Item {id}</p> : null}
    </div>
  );
}
